import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import forwadarrow from "../../../src/assets/images/arrow_forward.svg";
import backarrow from "../../../src/assets/images/back-arrow-left.svg";
import daterange from "../../../src/assets/images/date_range.svg";
import downloadicon from "../../../src/assets/images/downloadblack.svg";
import ellipsis from "../../../src/assets/images/eclips.svg";
import editicon from "../../../src/assets/images/editicon.svg";
import useLanguageTranslate from "../../hooks/useLanguageTranslate";
import useScreenResolution from "../../hooks/useScreenResolution";
import {
  getPolicyViewHeaderResponse,
  getPolicyViewLoading,
} from "../../redux/features/policyDetailsSlice";
import { selectProfileData } from "../../redux/features/profileSlice";
import { getPolicyDisplayInfo } from "../../utils/policyCardUtils";
import BackdropModal from "../common/modal/backdropModal";
import PopoverComponent from "../common/popover/PopoverComponent";
import EditableField from "./editableField";
import { POLICY_TYPE_ROUTES } from "../../pages/renewalPages/data";
const menuData = [
  // {
  //   icon: editicon,
  //   text_en: "Edit plan Name",
  //   text_ar: "تحرير اسم الوثيقة",
  //   type: "edit",
  // },
  {
    icon: downloadicon,
    text_en: "Download Documents",
    text_ar: "تحميل المستندات",
    type: "download",
  },
];

const NonMotorPolicyCard = ({
  content,
  handleDownloadMenuClicked,
  car,
  getPolicyDetails,
  isRenewable: isRenewableProp, // IsRenewable from GetUserPolicies API
}) => {
  const translate = useLanguageTranslate();

  console.log("#####################", content, car, getPolicyDetails);

  // const dispatch = useDispatch();
  // const PolicyViewReq = {
  //   PolicyNumber: "P/003/14/20/8003/0020575-02",
  //   Channel: "online.solidarity"
  // }
  // const PolicyViewresDetails = useSelector(PolicyViewResponse);
  // console.log("CHeck: " + PolicyViewresDetails["PolicyHeader"]["Make"]);
  const [isEditing, setIsEditing] = useState(false);
  const [isMobileEditing, setIsMobileEditing] = useState(false);
  const [showEllipsisMenu, setShowEllipsisMenu] = useState(false);
  const screenResolution = useScreenResolution();
  const policyDetails = useSelector(getPolicyViewHeaderResponse);
  const policyViewLoading = useSelector(getPolicyViewLoading);
  const profileData = useSelector(selectProfileData);
  // Use IsRenewable from GetUserPolicies API (prop) instead of policyView API.
  // Restrict the Renew button to product codes that have a working renewal flow.
  const RENEWABLE_PRODUCT_CODES = ["0101", "0710", "0204", "0222", "0640", "0202"];
  const productCodeForRenewal =
    policyDetails?.ProductCode || getPolicyDetails?.ProductCode;
  const isRenewable =
    isRenewableProp && RENEWABLE_PRODUCT_CODES.includes(productCodeForRenewal);
  // Function to calculate travel duration
  const calculateTravelDuration = () => {
    if (!policyDetails?.FromDateStr || !policyDetails?.ToDateStr) return null;

    try {
      // Parse dates in DD/MM/YYYY format
      const fromParts = policyDetails.FromDateStr.split("/");
      const toParts = policyDetails.ToDateStr.split("/");

      const fromDate = new Date(fromParts[2], fromParts[1] - 1, fromParts[0]);
      const toDate = new Date(toParts[2], toParts[1] - 1, toParts[0]);

      const timeDiff = toDate.getTime() - fromDate.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

      // Travel duration should be return date - departure date + 1
      return daysDiff > 0 ? daysDiff + 1 : null;
    } catch (error) {
      console.error("Error calculating travel duration:", error);
      return null;
    }
  };

  // Get policy-specific display information based on product code
  const policyDisplayInfo = getPolicyDisplayInfo(policyDetails);
  // Check if this is AMAN insurance (ProductCode 0913 or 0914)
  const isAmanInsurance =
    policyDetails?.ProductCode === "0913" ||
    policyDetails?.ProductCode === "0914";

  // Handle renewal navigation - redirect to specific renewal screen based on product type
  const handleRenewalClick = () => {
    const productCode = policyDetails?.ProductCode;
    const policyNumber = policyDetails?.PolicyNumber || getPolicyDetails?.PolicyNumber;

    // Get the specific renewal route for this product type
    const renewalRoute = POLICY_TYPE_ROUTES[productCode];

    if (renewalRoute && policyNumber) {
      // Navigate to specific renewal screen with policy data.
      // Renewal pages (e.g. RenewalHomePolicy) read `selectedPolicy` from
      // location.state, matching what GenericSelectPoliciesPage passes.
      navigate(renewalRoute, {
        state: {
          selectedPolicy: policyDetails,
          policyNumber: policyNumber,
          policyDetails: policyDetails,
        },
      });
    } else {
      // Fallback to generic renewal page if product doesn't have specific renewal flow
      console.warn(`No specific renewal route for product code: ${productCode}`);
      navigate("/renew", {
        state: {
          selectedPolicy: policyDetails,
          policyNumber: policyNumber,
          policyDetails: policyDetails,
        },
      });
    }
  };

  const policyInfo = (() => {
    if (!getPolicyDetails) {
      return translate(
        "No Policy Details Available",
        "لا توجد تفاصيل عن الوثيقة",
      );
    }

    // For travel insurance, show duration in days
    if (policyDetails?.ProductCode === "0204") {
      const duration = calculateTravelDuration();
      if (duration) {
        return translate(`${duration} Days`, `${duration} أيام`);
      }
    }

    // For home insurance, show Building number and Block number
    if (policyDetails?.ProductCode === "0710") {
      const buildingNo = policyDetails?.Building || "N/A";
      const blockNo = policyDetails?.Block || "N/A";
      return translate(
        `Building ${buildingNo}, Block ${blockNo}`,
        `مبنى ${buildingNo}، مربع ${blockNo}`,
      );
    }

    // For domestic helper insurance, show helper name
    if (policyDetails?.ProductCode === "0222") {
      const helperName = policyDetails?.Name || "N/A";
      return helperName;
    }

    // For smart device insurance, show device type name
    if (
      policyDetails?.ProductCode === "0213" ||
      policyDetails?.ProductCode === "0205"
    ) {
      const deviceTypeName =
        policyDetails?.DeviceTypeName ||
        policyDetails?.DeviceType ||
        policyDetails?.DeviceName ||
        "N/A";
      return deviceTypeName;
    }

    // For personal accident insurance, show CPR from profile
    if (
      policyDetails?.ProductCode === "0202" ||
      policyDetails?.ProductCode === "0208"
    ) {
      const cpr = profileData?.CPR || policyDetails?.CPR || "N/A";
      return cpr;
    }

    // For personal cyber insurance, show CPR + FirstName + LastName
    if (policyDetails?.ProductCode === "0206") {
      const cpr = profileData?.CPR || policyDetails?.CPR || "";
      const firstName =
        profileData?.FirstName || policyDetails?.Party?.FirstName || "";
      const lastName =
        profileData?.LastName || policyDetails?.Party?.LastName || "";
      const fullInfo = `${cpr} ${firstName} ${lastName}`.trim();
      return fullInfo || "N/A";
    }

    // Fallback: Check for personal cyber by name (in case different product code)
    const productName = policyDetails?.ProductName?.toLowerCase() || "";
    const planName = policyDetails?.PlanName?.toLowerCase() || "";
    if (
      productName.includes("personal cyber") ||
      productName.includes("cyber") ||
      planName.includes("personal cyber") ||
      planName.includes("cyber")
    ) {
      const cpr = profileData?.CPR || policyDetails?.CPR || "";
      const firstName =
        profileData?.FirstName || policyDetails?.Party?.FirstName || "";
      const lastName =
        profileData?.LastName || policyDetails?.Party?.LastName || "";
      const fullInfo = `${cpr} ${firstName} ${lastName}`.trim();
      return fullInfo || "N/A";
    }

    if (isAmanInsurance) {
      return policyDetails?.InsuredCPR || policyDetails?.MemberCPR || "";
    }

    return `${policyDetails?.ProductName || ""}`;
  })();

  const [policyLabel, setPolicyLabel] = useState(policyInfo);

  //   useEffect(() => {
  //     dispatch(policyView(PolicyViewReq));
  //   }, [dispatch]);

  //  useEffect(() => {
  //   if (PolicyViewresDetails && PolicyViewresDetails.PolicyHeader) {
  //     console.log("CHeck: " + PolicyViewresDetails["PolicyHeader"]["Make"]);
  //     setPolicyLabel(PolicyViewresDetails["PolicyHeader"]["Make"]);
  //   }
  // }, [PolicyViewresDetails]);

  useEffect(() => {
    if (policyInfo) {
      const translatedLabel = translate(policyInfo, policyInfo);
      setPolicyLabel(translatedLabel);
    }
  }, [
    content,
    policyDetails?.FromDateStr,
    policyDetails?.ToDateStr,
    policyDetails?.ProductCode,
    policyDetails?.Block,
    policyDetails?.City,
    policyDetails?.Name,
    policyDetails?.DeviceTypeName,
    policyDetails?.DeviceType,
    policyDetails?.DeviceName,
    profileData?.CPR,
    profileData?.FirstName,
    profileData?.LastName,
  ]);

  useEffect(() => {
    if (!car) {
      setPolicyLabel("No policy Details Available");
      return;
    }

    let updatedLabel;
    // For travel insurance, show duration in days
    if (policyDetails?.ProductCode === "0204") {
      const duration = calculateTravelDuration();
      updatedLabel = duration
        ? translate(`${duration} Days`, `${duration} أيام`)
        : `${policyDetails?.ProductName || ""}`;
    }
    // For home insurance, show Building number and Block number
    else if (policyDetails?.ProductCode === "0710") {
      const buildingNo = policyDetails?.Building || "N/A";
      const blockNo = policyDetails?.Block || "N/A";
      updatedLabel = translate(
        `Building ${buildingNo}, Block ${blockNo}`,
        `مبنى ${buildingNo}، مربع ${blockNo}`,
      );
    }
    // For domestic helper insurance, show helper name
    else if (policyDetails?.ProductCode === "0222") {
      const helperName = policyDetails?.Name || "N/A";
      updatedLabel = helperName;
    }
    // For smart device insurance, show device type name
    else if (
      policyDetails?.ProductCode === "0213" ||
      policyDetails?.ProductCode === "0205"
    ) {
      const deviceTypeName =
        policyDetails?.DeviceTypeName ||
        policyDetails?.DeviceType ||
        policyDetails?.DeviceName ||
        "N/A";
      updatedLabel = deviceTypeName;
    }
    // For personal accident insurance, show CPR from profile
    else if (
      policyDetails?.ProductCode === "0202" ||
      policyDetails?.ProductCode === "0208"
    ) {
      const cpr = profileData?.CPR || policyDetails?.CPR || "N/A";
      updatedLabel = cpr;
    }
    // For personal cyber insurance, show CPR + FirstName + LastName
    else if (policyDetails?.ProductCode === "0206") {
      const cpr = profileData?.CPR || policyDetails?.CPR || "";
      const firstName =
        profileData?.FirstName || policyDetails?.Party?.FirstName || "";
      const lastName =
        profileData?.LastName || policyDetails?.Party?.LastName || "";
      const fullInfo = `${cpr} ${firstName} ${lastName}`.trim();
      updatedLabel = fullInfo || "N/A";
    } else {
      // Fallback: Check for personal cyber by name (in case different product code)
      const productName = policyDetails?.ProductName?.toLowerCase() || "";
      const planName = policyDetails?.PlanName?.toLowerCase() || "";
      if (
        productName.includes("personal cyber") ||
        productName.includes("cyber") ||
        planName.includes("personal cyber") ||
        planName.includes("cyber")
      ) {
        const cpr = profileData?.CPR || policyDetails?.CPR || "";
        const firstName =
          profileData?.FirstName || policyDetails?.Party?.FirstName || "";
        const lastName =
          profileData?.LastName || policyDetails?.Party?.LastName || "";
        const fullInfo = `${cpr} ${firstName} ${lastName}`.trim();
        updatedLabel = fullInfo || "N/A";
      } else if (isAmanInsurance) {
        updatedLabel = car?.DomesticHelper?.CPR ||
          policyDetails?.InsuredCPR || policyDetails?.MemberCPR || "";
      } else {
        updatedLabel = `${policyDetails?.ProductName || ""}`;
      }
    }

    setPolicyLabel(updatedLabel);
  }, [
    car,
    policyDetails?.FromDateStr,
    policyDetails?.ToDateStr,
    policyDetails?.ProductCode,
    isAmanInsurance,
    policyDetails?.Block,
    policyDetails?.City,
    policyDetails?.Name,
    policyDetails?.DeviceTypeName,
    policyDetails?.DeviceType,
    policyDetails?.DeviceName,
    policyDetails?.InsuredName,
    profileData?.CPR,
    profileData?.FirstName,
    profileData?.LastName,
  ]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSaveClick = () => {
    setIsEditing(false);
  };
  const handleMobileSaveClick = () => {
    toggleMobileEditing();
  };

  const navigate = useNavigate();
  const backClickHandler = () => {
    navigate("/my-policy");
  };

  const { t } = useTranslation();

  const toggleEllipsisMenu = (e) => {
    e.stopPropagation(); // Prevent click event from propagating to other elements
    setShowEllipsisMenu(!showEllipsisMenu);
  };

  const toggleMobileEditing = () => {
    setIsMobileEditing(!isMobileEditing);
  };

  const handleMenuClick = (type) => {
    if (type == "edit") {
      console.log("edit");
      setIsMobileEditing(true);
    } else if (type == "download") {
      console.log("download");
      if (typeof handleDownloadMenuClicked === "function") {
        handleDownloadMenuClicked(true);
      }
    }
    setShowEllipsisMenu(false);
  };

  const mobileEditableField = (
    <>
      <div className="editmodal">
        <EditableField
          value={
            policyViewLoading
              ? translate(
                  "Getting policy details ...",
                  "جاري استرجاع تفاصيل الوثيقة ...",
                )
              : policyLabel
          }
          isEditing={false}
          isMobile={screenResolution.mobile} // Pass isMobile prop
        />
        <div className="editpolicymodal">
          <BackdropModal
            show={isMobileEditing}
            onClose={toggleMobileEditing}
            title={translate("Edit plan Name", "تحرير اسم الوثيقة")}
          >
            <EditableField
              value={
                policyViewLoading
                  ? translate(
                      "Getting policy details ...",
                      "جاري استرجاع تفاصيل الوثيقة ...",
                    )
                  : policyLabel
              }
              onChange={(e) => setPolicyLabel(e.target.value)}
              isEditing={isMobileEditing}
              onSave={handleMobileSaveClick}
              isMobile={screenResolution.mobile}
            />
          </BackdropModal>
        </div>
      </div>
    </>
  );

  return (
    <>
      <div className="visible flex   md:hidden   items-start mb-4 max767:flex max767:w-full max767:flex-row ipad:w-[55%] max767:sticky max767:top-0 max767:mt-0 max767:bg-[#EDFAFF] max767:p-4 stickytitle max767:items-center max767:mb-0 max767:z-10">
        <button
          onClick={backClickHandler}
          className="flex text-black text-sm not-italic font-semibold leading-6 tracking-[-0.084px] max767:bg-[#F6F8F9] max767:p-2 max767:rounded-3xl"
        >
          <img
            src={backarrow}
            alt="back arrow"
            className="rtl:rotate-180 rtl:mr-1"
          />
          <span className="max767:hidden rtl:mr-1">
            {" "}
            {t("motorInsurance.policyOverview.back")}
          </span>
        </button>
        {/* <div className="flex flex-col items-start mb-4 max-600:block max-600:w-full max767:mb-0">
          {screenResolution.mobile ? (
            mobileEditableField
          ) : (
            <EditableField
              value={
                policyViewLoading
                  ? translate(
                      "Getting policy details ...",
                      "جاري استرجاع تفاصيل الوثيقة..."
                    )
                  : ""
              }
              onChange={(e) => setPolicyLabel(e.target.value)}
              isEditing={isEditing}
              onSave={handleSaveClick}
              onEdit={handleEditClick}
              editIcon={editicon}
              isMobile={screenResolution.mobile}
              className="max767:hidden"
            />
          )}
        </div> */}
        <div className="flex flex-col items-start mb-4 max-600:block max-600:w-full max767:mb-0"></div>
        {content?.btn && isRenewable && !policyViewLoading && (
          <button
            className="flex items-center gap-2 bg-[#55C6EF] px-3 py-1 rounded-md text-[#48535B] text-sm not-italic font-normal leading-6  max767:hidden max767:mt-4 max767:w-40"
            onClick={handleRenewalClick}
          >
            {content?.btn?.icon && (
              <img src={content?.btn?.icon} alt="" className="h-6 w-6" />
            )}
            <span>
              {translate(
                content?.btn?.btn_lable_en,
                content?.btn?.btn_lable_ar,
              )}
            </span>
          </button>
        )}

        {screenResolution.mobile && (
          <PopoverComponent
            headerMenu={
              <button
                onClick={(e) => toggleEllipsisMenu(e)} // Pass the event to the handler
                className="flex text-black text-sm not-italic font-semibold leading-6 tracking-[-0.084px]"
              >
                <img alt="ellipsis" src={ellipsis} />
              </button>
            }
            positions={["bottom"]}
            isPopoverOpen={showEllipsisMenu}
            closer={toggleEllipsisMenu}
            popupContent={
              <div className="flex w-[228px] flex-col items-start bg-white shadow-[0px_0px_32px_0px_rgba(0,0,0,0.20)] rounded-xl">
                {menuData.map((data) => (
                  <button
                    key={data.type}
                    className=" flex h-11 justify-between items-center self-stretch px-4 py-0 border-b-[#DDE2E4] border-b border-solid"
                    onClick={() => handleMenuClick(data.type)}
                  >
                    {translate(data.text_en, data.text_ar)}
                    <img src={data.icon} alt="" />
                  </button>
                ))}
              </div>
            }
          />
        )}
      </div>
      <div className="p-6 bg-[#EDFAFF] rounded-2xl flex justify-between items-start self-stretch mt-6 mb-3 max767:flex-col max767:mt-0 max767:rounded-none max767:pb-4 max767:p-4">
        <div className="flex flex-col  items-start mb-4 max767:hidden max767:w-full max767:flex-row ipad:w-[55%] max767:sticky max767:top-0 max767:mt-0 max767:bg-[#EDFAFF] max767:p-0 stickytitle max767:items-center max767:mb-3">
          <button
            onClick={backClickHandler}
            className="flex text-black text-sm not-italic font-semibold leading-6 tracking-[-0.084px] max767:bg-[#F6F8F9] max767:p-2 max767:rounded-3xl"
          >
            <img
              src={backarrow}
              alt="back arrow"
              className="rtl:rotate-180 rtl:mr-1"
            />
            <span className="max767:hidden rtl:mr-1">
              {" "}
              {t("motorInsurance.policyOverview.back")}
            </span>
          </button>
          <div className="flex flex-col items-start mb-4 max-600:block max-600:w-full max767:mb-0">
            {screenResolution.mobile ? (
              mobileEditableField
            ) : (
              <EditableField
                value={
                  policyViewLoading
                    ? translate(
                        "Getting policy details ...",
                        "جاري استرجاع تفاصيل الوثيقة ...",
                      )
                    : policyLabel
                }
                onChange={(e) => setPolicyLabel(e.target.value)}
                isEditing={isEditing}
                onSave={handleSaveClick}
                onEdit={handleEditClick}
                editIcon={editicon}
                isMobile={screenResolution.mobile}
              />
            )}
          </div>
          {content?.btn && isRenewable && !policyViewLoading && (
            <button
              className="flex items-center gap-2 bg-[#55C6EF] px-3 py-1 rounded-md text-[#1A2024] text-sm not-italic font-normal leading-6  max767:hidden max767:mt-4 max767:w-40"
              onClick={handleRenewalClick}
            >
              {content?.btn?.icon && (
                <img src={content?.btn?.icon} alt="" className="h-6 w-6" />
              )}
              <span>
                {translate(
                  content?.btn?.btn_lable_en,
                  content?.btn?.btn_lable_ar,
                )}
              </span>
            </button>
          )}
        </div>

        <div className="flex flex-col justify-between items-start mb-4 max767:w-full max767:mt-0">
          <span className="text-sm not-italic font-semibold flex items-center mb-4 gap-1 bg-[#F6F8F9] px-3 py-1 rounded-md text-[#252C32] max767:mb-1 max767:w-max max767:text-[#252C32] ">
            {(() => {
              const status = policyDetails?.PolicyStatus;
              if (status === "ACTIVE POLICY")
                return translate("Active", "نشيط");
              if (status === "EXPIRED")
                return translate("Expired", "منتهية الصلاحية");
              if (status === "CANCELLED")
                return translate("Cancelled", "ملغاة");
              return translate(status, content?.status_ar);
            })()}
          </span>
          {screenResolution.mobile && mobileEditableField}
          <div className="mt-2">
            {/* AMAN Insurance specific layout */}
            {isAmanInsurance ? (
              <>
                {/* Plan Name */}
                <p className="text-[#48535B] text-sm not-italic font-semibold leading-[normal] mb-2">
                  {policyDetails?.ProductCode === "0914" 
                    ? translate("Aman Plus", "أمَانْ Plus") 
                    : translate("Aman", "أمَانْ")}
                </p>
                {/* Policy Number */}
                <p className="text-[#48535B] text-sm not-italic leading-[normal] mb-2 max767:text-base break-all">
                  {translate("Policy Number:", "رقم الوثيقة:")} {policyDetails?.PolicyNumber}
                </p>
              </>
            ) : (
              <>
                {/* Insurance type-specific policy name - universal for all insurance types */}
                {policyDisplayInfo?.showInCard && policyDisplayInfo.title && (
                  <p className="text-[#48535B] text-sm not-italic font-semibold leading-[normal] mb-2">
                    {translate(
                  policyDisplayInfo.title,
                  policyDisplayInfo.title_ar || policyDisplayInfo.title,
                )}
                    {policyDisplayInfo.subtitle && (
                      <>
                        {" - "}
                        {translate(
                      policyDisplayInfo.subtitle,
                      policyDisplayInfo.subtitle_ar ||
                        policyDisplayInfo.subtitle,
                    )}
                      </>
                    )}
                  </p>
                )}
                <p className="text-[#48535B] text-sm not-italic leading-[normal] mb-2 max767:text-base break-all">
                  {translate("Policy Number:", "رقم الوثيقة:")}{" "}
              {policyDetails?.PolicyNumber}
                </p>
              </>
            )}
            <span className="flex items-center gap-4 w-[255px] bg-white p-2 rounded-md text-[#48535B] text-sm not-italic font-semibold leading-[120%] max767:w-fit max767:text-sm">
              <img src={daterange} alt="date select" />{" "}
              {`${policyDetails?.FromDateStr.split("/")[0] ?? ""}/${
                policyDetails?.FromDateStr.split("/")[1] ?? ""
              }/${policyDetails?.FromDateStr.split("/")[2] ?? ""}`}
              <img
                src={forwadarrow ?? ""}
                alt="date select"
                className="rtl:rotate-180"
              />{" "}
              {`${policyDetails?.ToDateStr.split("/")[0] ?? ""}/${
                policyDetails?.ToDateStr.split("/")[1] ?? ""
              }/${policyDetails?.ToDateStr.split("/")[2] ?? ""}`}
            </span>
            {content?.btn && isRenewable && !policyViewLoading && (
              <button
                className="flex items-center gap-2 bg-[#55C6EF] px-3 py-2 rounded-md text-[#48535B] text-sm not-italic font-normal leading-6 hidden max767:flex max767:mt-4 max767:w-full max767:justify-center"
                onClick={handleRenewalClick}
              >
                {content?.btn?.icon && (
                  <img src={content?.btn?.icon} alt="" className="h-6 w-6" />
                )}
                <span>
                  {translate(
                    content?.btn?.btn_lable_en,
                    content?.btn?.btn_lable_ar,
                  )}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default NonMotorPolicyCard;
